import { Avatar, Stack, TableRow, Theme, Tooltip, Typography } from '@mui/material';
import { GetGroupsQuerySort, Group, SORT_DIRECTION, User } from '@schooly/api';
import { AvatarAuth } from '@schooly/components/avatar-auth';
import { SchoolPeriodLabel } from '@schooly/components/filters';
import { DATE_FORMAT } from '@schooly/constants';
import { LIST_ROW_COLUMNS, TABLE } from '@schooly/constants/test-ids';
import { useRenderGroupType } from '@schooly/hooks/use-render-group-type';
import {
  ArchiveIcon,
  GridCell,
  GridHead,
  SortableCell,
  TruncatedText,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import useAppLocation from '../../hooks/useAppLocation';
import { AgeGroupLabel } from './AgeGroupLabel';

type GroupsHeaderProps = {
  sort?: GetGroupsQuerySort;
  onChangeSort: (v: GetGroupsQuerySort) => void;
  rightIcon?: ReactNode;
};

export const GroupsHeader: FC<GroupsHeaderProps> = ({ onChangeSort, sort, rightIcon }) => {
  const { formatMessage } = useIntl();

  const handleSort = (columnTextId: GetGroupsQuerySort['columnTextId']) => () => {
    onChangeSort({
      columnTextId,
      direction:
        columnTextId === sort?.columnTextId
          ? sort.direction === SORT_DIRECTION.ASC
            ? SORT_DIRECTION.DESC
            : SORT_DIRECTION.ASC
          : SORT_DIRECTION.ASC,
    });
  };

  return (
    <GridHead borderBottom>
      <SortableCell
        label={formatMessage({ id: 'groups-GroupName' })}
        sort={sort}
        columnTextId="name"
        onChangeSort={handleSort}
        pl={2}
        width="50%"
      />
      <SortableCell
        label={formatMessage({ id: 'userType-student-plural' })}
        sort={sort}
        columnTextId="students"
        onChangeSort={handleSort}
        width="180px"
        sx={(theme) => ({
          [theme.breakpoints.only('md')]: {
            width: 140,
          },
        })}
      />
      <SortableCell
        label={formatMessage({ id: 'userType-staff-plural' })}
        sort={sort}
        columnTextId="staff"
        onChangeSort={handleSort}
        width="180px"
        sx={(theme) => ({
          [theme.breakpoints.only('md')]: {
            width: 140,
          },
        })}
      />
      <SortableCell
        label={formatMessage({ id: 'groups-GroupSubject' })}
        sort={sort}
        columnTextId="subject"
        onChangeSort={handleSort}
        width="25%"
      />
      <GridCell width="25%">{formatMessage({ id: 'groups-GroupAgeGroup' })}</GridCell>
      <SortableCell
        label={formatMessage({ id: 'groups-Validity' })}
        sort={sort}
        columnTextId="validity"
        onChangeSort={handleSort}
        width="180px"
        sx={(theme) => ({
          [theme.breakpoints.only('md')]: {
            width: 140,
          },
        })}
      />
      <GridCell right pr={1} width="40px">
        {rightIcon}
      </GridCell>
    </GridHead>
  );
};

type GroupRowProps = {
  group: Group;
  periodLabel?: string;
};

export const GroupRow: FC<GroupRowProps> = ({ group, periodLabel }) => {
  const location = useAppLocation();
  const { formatMessage } = useIntl();
  const renderGroupType = useRenderGroupType();

  const dateFrom = group.validity.date_from;
  const dateTo = group.validity.date_to;

  const validity =
    dateFrom && dateTo
      ? `${format(newDateTimezoneOffset(dateFrom), DATE_FORMAT)} - ${format(
          newDateTimezoneOffset(dateTo),
          DATE_FORMAT,
        )}`
      : '—';

  return (
    <TableRow
      component={Link}
      to={`/groups/${group.id}`}
      state={{ initialState: group, backgroundLocation: location }}
      sx={(theme) => ({
        '&:hover td': {
          backgroundColor: theme.palette.background.default,
        },
      })}
      data-test-id={TABLE.ROW}
    >
      <GridCell pl={2} data-test-id={LIST_ROW_COLUMNS.group_name}>
        <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
          {group.name}
        </TypographyWithOverflowHint>
      </GridCell>
      <GridCell>
        <StudentsCellContent group={group} />
      </GridCell>
      <GridCell>
        <StaffCellContent group={group} />
      </GridCell>
      <GridCell data-test-id={LIST_ROW_COLUMNS.subject}>
        {group.subject?.archived ? (
          <Tooltip title={formatMessage({ id: 'schoolProperty-Archived-subject' })}>
            <Typography color="text.primary">
              <ArchiveIcon /> {renderGroupType(group) ?? '—'}
            </Typography>
          </Tooltip>
        ) : (
          <Typography color="text.primary">{renderGroupType(group) ?? '—'}</Typography>
        )}
      </GridCell>
      <GridCell data-test-id={LIST_ROW_COLUMNS.age_groups}>
        <AgeGroupsCellContent group={group} />
      </GridCell>
      <GridCell>
        {periodLabel ? (
          <SchoolPeriodLabel label={periodLabel} dateRange={validity} />
        ) : (
          <Typography color="text.primary">{validity}</Typography>
        )}
      </GridCell>
      <GridCell />
    </TableRow>
  );
};

const AVATARS_AMOUNT = 3;

type CellContentProps = {
  group: Group;
};
const StudentsCellContent: FC<CellContentProps> = ({ group }) => {
  const users = group.students_preview.slice(0, AVATARS_AMOUNT);
  const restAmount = Math.max(0, group.student_count - AVATARS_AMOUNT);

  return (
    <Stack flexDirection="row" gap={1} alignItems="center">
      <Typography color="text.primary">{group.student_count || '—'}</Typography>
      <Avatars users={users} restAmount={restAmount} />
    </Stack>
  );
};
const StaffCellContent: FC<CellContentProps> = ({ group }) => {
  const users = group.staff_preview.slice(0, AVATARS_AMOUNT);
  const restAmount = Math.max(0, group.staff_count - AVATARS_AMOUNT);

  return (
    <Stack flexDirection="row" gap={1} alignItems="center">
      <Typography color="text.primary">{group.staff_count || '—'}</Typography>
      <Avatars users={users} restAmount={restAmount} />
    </Stack>
  );
};

type AvatarsProps = {
  users: Pick<User, 'profile_picture' | 'given_name' | 'last_name' | 'user_id'>[];
  restAmount: number;
};
export const Avatars: FC<AvatarsProps> = ({ users, restAmount }) => {
  return (
    <Stack
      sx={(theme) => ({
        my: theme.spacing(-1),
        flexDirection: 'row',
        '.MuiAvatar-root + .MuiAvatar-root': {
          marginLeft: theme.spacing(-1),
        },
      })}
    >
      {users.map((u) => {
        return <AvatarAuth key={u.user_id} user={u} />;
      })}
      {!!restAmount && <Avatar sx={{ fontSize: 10 }}>+{restAmount}</Avatar>}
    </Stack>
  );
};

export const AgeGroupsCellContent: FC<CellContentProps> = ({ group }) => {
  const sortedAgeGroups = useMemo(
    () => [...(group.age_groups ?? [])].sort((a, b) => a.order - b.order),
    [group.age_groups],
  );

  if (!sortedAgeGroups.length) return <Typography color="text.primary">{'—'}</Typography>;

  const renderAgeGroup = (index: number) => {
    const ageGroup = sortedAgeGroups[index];
    if (!ageGroup) return null;

    return <AgeGroupLabel ageGroup={ageGroup} />;
  };

  return (
    <TruncatedText
      maxLines={1}
      items={sortedAgeGroups.map((ag) => ag.name)}
      renderItem={renderAgeGroup}
      typographyProps={{ color: 'text.primary' }}
      tooltipProps={{
        arrow: false,
        placement: 'bottom-start',
        title: (
          <Stack>
            {sortedAgeGroups.map((ag) => (
              <Typography key={ag.id} color="text.primary">
                <AgeGroupLabel ageGroup={ag} />
              </Typography>
            ))}
          </Stack>
        ),
        componentsProps: {
          tooltip: {
            sx: { px: 0.5, py: 0.25, minWidth: 100, maxHeight: 400, overflowY: 'scroll' },
          },
        },
        slotProps: {
          popper: {
            sx: (theme: Theme) => ({
              '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                margin: theme.spacing(-2.5, 0, 0, -0.5),
              },
              '&.MuiTooltip-popper[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                margin: theme.spacing(0, 0, -2.5, -0.5),
              },
            }),
          },
        },
      }}
    />
  );
};
